import { useEffect, useState } from 'react';
import { companyApi } from '../services/api';
import type { Company } from '../types';
import { isPracticalInfoComplete } from '../utils/practicalInfo';

const COMPANY_UPDATED_EVENT = 'etappio:company-updated';

// One request per signed-in user and page load, shared by every layout render;
// cleared when the company is saved so the menu reflects the new state.
let cached: { userId: string; company: Promise<Company | null> } | null = null;

const loadCompany = (userId: string) => {
  if (!cached || cached.userId !== userId) {
    cached = { userId, company: companyApi.getCompany().catch(() => null) };
  }
  return cached.company;
};

/** Call after the company profile has been saved. */
export function notifyCompanyUpdated(): void {
  cached = null;
  window.dispatchEvent(new Event(COMPANY_UPDATED_EVENT));
}

/**
 * Whether the company profile still lacks practical information (same rule as
 * the start page's todo item). Contact and practical details are skipped in a
 * self-registered onboarding, so the menu keeps a reminder until they are
 * completed under Inställningar → Företagsuppgifter.
 */
export function useCompanyProfileAttention(userId: string | undefined): boolean {
  const [attention, setAttention] = useState(false);

  useEffect(() => {
    if (!userId) {
      setAttention(false);
      return;
    }
    let active = true;
    const refresh = () => {
      void loadCompany(userId).then(company => {
        if (active) setAttention(Boolean(company) && !isPracticalInfoComplete(company));
      });
    };
    refresh();
    window.addEventListener(COMPANY_UPDATED_EVENT, refresh);
    return () => {
      active = false;
      window.removeEventListener(COMPANY_UPDATED_EVENT, refresh);
    };
  }, [userId]);

  return attention;
}
